import React, { memo, useState } from 'react';
import { Menu } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/ModuleContext';
import { useTheme, DOCK_SIZES } from '../../context/ThemeContext';
import NotificationBell from '../notifications/NotificationBell';
import { useCommandPalette } from '../../context/CommandPaletteContext';
import { moduleForPath } from '../../lib/moduleRoutes';
import { DOCK, dockKeyForPath, DockItem } from './dock';
import { UtilityBell, UtilityMoon, UtilityPlus, UtilitySearch, UtilitySun } from './dockIcons';
import { NAV } from './Sidebar';
import { UserRole } from '../../types';

interface TopbarProps {
  onToggleSidebar: () => void;
  collapsed: boolean;
  isMobile?: boolean;
}

/**
 * Mockup-01 app shell: ONE dock in the header.
 *   Single frosted pill: logo + every feature module + separator + the
 *   utility controls (Admin, search, + New, notifications, account).
 * No breadcrumbs anywhere — removed app-wide per Lis's 13 Aug UI review;
 * every page starts at its own title.
 *
 * Centering (PR #38's measured guard): both flanks are flex-1 (basis 0), so
 * each grows to exactly (width − dock) / 2 and the dock's midpoint sits on
 * the page midline. A flank never shrinks below its own content, so under
 * pressure the dock degrades to off-centre — never an overlap.
 */
const Topbar = memo(function Topbar({ onToggleSidebar }: TopbarProps) {
  const { profile } = useAuth();
  const { canAccess: canOpenModule } = useModules();
  const { t } = useTranslation();
  const { open: openPalette } = useCommandPalette();
  const { colorMode, setColorMode, look, lookForcesMode, dockSize, motionReduced } = useTheme();
  const navigate = useNavigate();

  /**
   * MAGNIFY — which module tile the pointer is over. The neighbours read this to
   * work out their own scale, which is why it is one piece of state on the dock
   * rather than per-tile `:hover` (CSS cannot express "the tile NEXT to the hovered
   * one"). React owns it; no document-level listeners.
   */
  const [magnifyIdx, setMagnifyIdx] = useState<number | null>(null);
  /* Accessibility + preference: `motionReduced` (from ThemeContext, above) is the
     app's ONE combined verdict — the OS prefers-reduced-motion setting (still
     live-subscribed, now inside ThemeContext) OR the in-app Motion & Animations
     toggles in Settings → Appearance. Any one of them pins the dock to no-grow.
     Tooltips still appear, so nothing is lost — only the movement. */
  const location = useLocation();
  const activeDock = dockKeyForPath(location.pathname);

  /** Every feature tile, Admin included, lives in the single dock (Lis approved
      Admin as a full-size tile, 2026-08-06). Tiles the person has no module
      access to are hidden (per-user access, migration 0151). */
  const MODULE_TILES = DOCK.filter(item => {
    if (!item.modules || item.modules.length === 0) return true;
    return item.modules.some(canOpenModule);
  });

  /**
   * The macOS-style falloff, RELATIVE to whatever base size the person chose — so
   * the effect feels the same at Compact as at Large.
   *
   * Honours `prefers-reduced-motion`: with it set, every tile stays at 1 and the
   * dock simply doesn't grow. The tooltips still appear, so nothing is lost —
   * only the movement is.
   *
   * `scale()` and NOT width/height on purpose: a transform doesn't change the
   * layout box, so the dock tray never re-lays-out and the frosted pill behind it
   * is never asked to repaint mid-hover. That is the mechanism behind the white
   * streaks this shell has been bitten by before.
   */
  const magnifyScale = (d: number): number => (d === 0 ? 1.55 : d === 1 ? 1.28 : d === 2 ? 1.1 : 1);

  /**
   * The transform for one tile: how much it grows, and how far it steps aside.
   *
   * The STEP ASIDE is the part that stops tiles overlapping. A `scale()` grows a
   * tile about its own centre without changing its layout box, so without this the
   * hovered tile simply covers its neighbours. Each neighbour is therefore pushed
   * outward by however much of it the growth would have swallowed — the row parts
   * around the pointer like the macOS dock.
   *
   * Everything here is still transform-only: the tray's layout never changes, so
   * the frosted pill behind it is never asked to repaint (PR #74's streak fix).
   *
   * `transform-origin: top center` (set below) anchors each tile's TOP edge, so the
   * growth travels DOWNWARD into the space reserved under the dock instead of off
   * the top of the page.
   */
  const magnifyTransform = (i: number): string => {
    if (motionReduced || magnifyIdx === null) return 'none';
    const signed = i - magnifyIdx;
    const d = Math.abs(signed);
    const B = DOCK_SIZES.find(s => s.id === dockSize)?.px ?? 44;
    const GAP = 7; // .pq-dock's gap — absorbs part of the growth before anything overlaps
    /* Half of each tile's growth reaches toward its neighbour; the existing gap
       covers some of it, and only the remainder has to be stepped around. */
    const half = (n: number) => ((magnifyScale(n) - 1) * B) / 2;
    const step1 = Math.max(0, half(0) + half(1) - GAP);
    const step2 = step1 + Math.max(0, half(1) + half(2) - GAP);
    const step3 = step2 + Math.max(0, half(2) - GAP);
    const shift = d === 0 ? 0 : Math.sign(signed) * (d === 1 ? step1 : d === 2 ? step2 : step3);
    return `translateX(${shift.toFixed(2)}px) scale(${magnifyScale(d)})`;
  };

  /** Can the current user see this nav entry? (same checks the sidebar uses) */
  const canAccess = (roles?: UserRole[]) =>
    !roles || (profile && roles.includes(profile.role as UserRole));

  const moduleAllows = (path?: string) => {
    if (!path) return true;
    const key = moduleForPath(path);
    return !key || canOpenModule(key);
  };

  /**
   * Resolve where a dock tile navigates: the first accessible page of its
   * context sections (respecting role + module gating), falling back to the
   * tile's default landing path.
   */
  const resolveDockPath = (item: DockItem): string => {
    for (const section of NAV) {
      if (!item.sections.includes(section.label)) continue;
      if (!canAccess(section.roles)) continue;
      if (section.moduleKey && !canOpenModule(section.moduleKey)) continue;
      if (section.path && moduleAllows(section.path)) return section.path;
      if (section.children) {
        for (const child of section.children) {
          if (canAccess(child.roles) && moduleAllows(child.path)) return child.path;
        }
      }
    }
    return item.defaultPath;
  };

  return (
    <header
      className="pq-topbar fixed top-0 left-0 right-0 h-16 flex items-center justify-between gap-2 z-40 transition-all duration-300"
    >
      {/* ── Left flank — flex-1 so the dock centres on the page midline ── */}
      <div className="flex flex-1 items-center">
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          className="pq-icon-btn"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* ── Center: the ONE dock (mockup 01) — content-sized, flanks do the centring ── */}
      <div className="hidden lg:flex min-w-0 justify-center">
        <nav className="pq-dock" aria-label="Modules">
          {/* The Home tile is gone: it navigated to /dashboard, which the Dashboard
              tile — now the first item — already does. Two controls for one
              destination is one too many, and it cost the dock a tile of width. */}

          {MODULE_TILES.map((item, i) => (
            <button
              key={item.key}
              type="button"
              onClick={() => navigate(resolveDockPath(item))}
              /* Pointer drives the magnify; focus does too, so keyboard users get the
                 same feedback as the mouse without needing to hover. */
              onMouseEnter={() => setMagnifyIdx(i)}
              onMouseLeave={() => setMagnifyIdx(null)}
              onFocus={() => setMagnifyIdx(i)}
              onBlur={() => setMagnifyIdx(null)}
              data-tip={t(item.labelKey, { defaultValue: item.fallback })}
              aria-label={t(item.labelKey, { defaultValue: item.fallback })}
              className={`pq-dock-ic pq-dock-app pq-dock-magnify ${activeDock === item.key ? 'on' : ''}`}
              style={{
                ['--dock-c' as string]: item.color,
                /* Inline so it beats the stylesheet's own hover rule — this is the one
                   place the tile's transform is decided. The dock is mounted at the TOP
                   of the page, so `top center` grows each tile DOWNWARD into the space
                   reserved beneath the tray; anchoring the bottom (as this first shipped)
                   pushed the growth up and off the top edge of the window. */
                transform: magnifyTransform(i),
                transformOrigin: 'top center',
              }}
            >
              {item.tile}
            </button>
          ))}

          <span className="pq-dock-sep" aria-hidden="true" />

          {/* Utility cluster — the approved order: bell → theme → +, grouped as
              quiet ghost-outline glyphs (Claude Design set, no coloured tiles),
              with the search bar LAST so it closes the dock at the far right. */}
          <div
            className="pq-dock-ic-wrap pq-dock-ctrl"
            data-tip="Notifications"
            data-tour="notification-bell"
          >
            <NotificationBell buttonClassName="pq-dock-ic" icon={UtilityBell} />
          </div>

          <button
            type="button"
            onClick={() => setColorMode(colorMode === 'light' ? 'dark' : 'light')}
            className="pq-dock-ic pq-dock-ctrl"
            disabled={lookForcesMode}
            style={lookForcesMode ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            data-tip={
              lookForcesMode
                ? `${look.charAt(0).toUpperCase()}${look.slice(1)} is a ${colorMode} look`
                : colorMode === 'light' ? 'Dark mode' : 'Light mode'
            }
            aria-label={lookForcesMode ? `Light or dark mode is set by the ${look} look` : 'Toggle light or dark mode'}
          >
            {/* Glyph shows the CURRENT state (approved design): sun while light,
                moon while dark. The tooltip still names the ACTION a click takes. */}
            {colorMode === 'light' ? UtilitySun : UtilityMoon}
          </button>

          {/*
            * Primary "New" action. TODO(redesign): wire to a real create menu
            * (new deal / ticket / task …). For now it opens the command palette
            * — the app's single command surface — so the control is functional.
            */}
          <button
            type="button"
            onClick={openPalette}
            className="pq-dock-ic pq-dock-ctrl"
            data-tip="New"
            aria-label="Create new"
          >
            {UtilityPlus}
          </button>

          <button
            type="button"
            onClick={openPalette}
            className="pq-dock-search"
            data-tour="topbar-search"
            aria-label="Search CBOps"
          >
            {UtilitySearch}
            <span className="pq-dock-search-txt">Search CBOps…</span>
            <kbd><span>⌘</span><span>K</span></kbd>
          </button>

          {/* No account tile: account (profile / settings / language / sign out)
              lives in the sidebar footer — one account surface, not two. */}
        </nav>
      </div>

      {/* ── Right flank — mirror of the left, keeps the dock page-centred ── */}
      <div className="flex flex-1 items-center justify-end" />
    </header>
  );
});

export default Topbar;
